
import React from 'react';

interface ContactSidebarProps {
  phone: string;
  email: string;
  website: string;
  address: string;
  name: string;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
  };
}

/**
 * ContactSidebar component displays contact details, social links,
 * a map preview and CTA buttons.
 *
 * The component validates the required props at compile‑time via TypeScript.
 * Runtime checks are added for optional values to avoid rendering broken links.
 */
export default function ContactSidebar({
  phone,
  email,
  website,
  address,
  name,
  socialMedia,
}: ContactSidebarProps) {
  // Helper to safely build URLs – prevents accidental `undefined` values.
  const safeUrl = (url?: string) => (url && url.trim() ? url.trim() : '#');

  // Encode the location string once for reuse.
  const locationQuery = encodeURIComponent(`${name} ${address}`);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm sticky top-16">
      <h3 className="text-base font-bold text-gray-900 mb-5">
        Contact Information
      </h3>

      <div className="space-y-4 mb-6">
        {/* Phone */}
        <a href={`tel:${phone}`} className="flex items-start gap-3 group cursor-pointer">
          <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-phone-line text-teal-600"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">Phone</p>
            <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
              {phone}
            </p>
          </div>
        </a>

        {/* Email */}
        <a
          href={`mailto:${email}`}
          className="flex items-start gap-3 group cursor-pointer"
          rel="nofollow"
        >
          <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-mail-line text-teal-600"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">Email</p>
            <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors break-all">
              {email}
            </p>
          </div>
        </a>

        {/* Website */}
        <a
          href={safeUrl(website)}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex items-start gap-3 group cursor-pointer"
        >
          <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-global-line text-teal-600"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">Website</p>
            <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors truncate max-w-[200px]">
              {website.replace(/^https?:\/\//, '')}
            </p>
          </div>
        </a>

        {/* Address */}
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-map-pin-line text-teal-600"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">Address</p>
            <p className="text-sm font-semibold text-gray-900">{address}</p>
          </div>
        </div>
      </div>

      {/* Social media */}
      {(socialMedia.facebook ||
        socialMedia.instagram ||
        socialMedia.linkedin ||
        socialMedia.twitter) && (
        <div className="pb-5 mb-5 border-b border-gray-100">
          <p className="text-xs text-gray-500 mb-3">Follow Us</p>
          <div className="flex items-center gap-2">
            {socialMedia.facebook && (
              <a
                href={safeUrl(socialMedia.facebook)}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-gray-500 hover:text-teal-600 text-lg"></i>
              </a>
            )}
            {socialMedia.instagram && (
              <a
                href={safeUrl(socialMedia.instagram)}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-instagram-fill text-gray-500 hover:text-teal-600 text-lg"></i>
              </a>
            )}
            {socialMedia.linkedin && (
              <a
                href={safeUrl(socialMedia.linkedin)}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-gray-500 hover:text-teal-600 text-lg"></i>
              </a>
            )}
            {socialMedia.twitter && (
              <a
                href={safeUrl(socialMedia.twitter)}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-gray-50 hover:bg-teal-50 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-fill text-gray-500 hover:text-teal-600 text-lg"></i>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Map */}
      <div className="mb-5">
        <div className="w-full h-48 rounded-xl overflow-hidden">
          <iframe
            title={`${name} location map`}
            src={`https://www.google.com/maps?q=${locationQuery}&output=embed`}
            className="w-full h-full border-0"
            loading="lazy"
            allowFullScreen
          ></iframe>
        </div>
      </div>

      {/* CTA buttons */}
      <div className="space-y-2.5">
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${locationQuery}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="block w-full bg-teal-600 text-white text-center py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-navigation-line mr-2"></i>
          Get Directions
        </a>
        <a
          href={`tel:${phone}`}
          className="block w-full bg-gray-100 text-gray-900 text-center py-3 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-line mr-2"></i>
          Call Business
        </a>
      </div>
    </div>
  );
}
